import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = ["aside", "collapseButton", "opener", "item", "backdrop"];
  static values = { activeClass: String, surface: String };

  connect() {
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this);
    this.boundSyncActiveState = this.syncActiveState.bind(this);

    document.addEventListener("turbo:frame-load", this.boundHandleFrameLoad);
    window.addEventListener("popstate", this.boundSyncActiveState);

    if (this.desktopSurface()) {
      this.boundSyncFromCookie = this.syncFromCookie.bind(this);
      this.boundHandleTabletChange = this.handleTabletChange.bind(this);
      this.boundHandleItemClick = this.handleItemClick.bind(this);

      // Tablet: 50em (800px) to 80em (1280px)
      this.tabletMediaQuery = window.matchMedia(
        "(width >= 50em) and (width < 80em)"
      );

      document.addEventListener("turbo:before-cache", this.boundSyncFromCookie);
      document.addEventListener("turbo:load", this.boundSyncFromCookie);
      this.tabletMediaQuery.addEventListener("change", this.boundHandleTabletChange);

      this.syncInitialState();
    }

    this.syncActiveState();
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundHandleFrameLoad);
    window.removeEventListener("popstate", this.boundSyncActiveState);

    if (this.boundSyncFromCookie) {
      document.removeEventListener("turbo:before-cache", this.boundSyncFromCookie);
      document.removeEventListener("turbo:load", this.boundSyncFromCookie);
    }

    if (this.tabletMediaQuery) {
      this.tabletMediaQuery.removeEventListener("change", this.boundHandleTabletChange);
    }
  }

  collapse(event) {
    this.applyCollapsedState(true, {
      persist: !this.isTablet(),
      focus: this.focusTargetFor(event, this.openerTarget),
    });
  }

  expand(event) {
    this.applyCollapsedState(false, {
      persist: !this.isTablet(),
      focus: this.focusTargetFor(event, this.collapseButtonTarget),
    });
  }

  handleFrameLoad(event) {
    if (event.target.id !== "community_main") return;

    this.syncActiveState();
  }

  // Called when entering/leaving the tablet range
  handleTabletChange() {
    if (this.isTablet()) {
      // Entering tablet: force collapsed
      this.applyCollapsedState(true);
    } else {
      // Leaving tablet: restore cookie state
      this.syncFromCookie();
    }
  }

  // Auto-close on nav item click (tablet only)
  handleItemClick(event) {
    if (!this.isTablet()) return;

    // Small delay so Turbo can start the navigation
    requestAnimationFrame(() => {
      this.applyCollapsedState(true);
    });
  }

  syncInitialState() {
    this.syncFromCookie();
  }

  syncFromCookie() {
    if (this.isTablet()) {
      this.applyCollapsedState(true);
    } else {
      this.applyCollapsedState(this.collapsedCookieValue() === "1");
    }
    this.syncActiveState();
  }

  applyCollapsedState(collapsed, { persist = false, focus = null } = {}) {
    document.body.classList.toggle("community-owner-navigation-collapsed", collapsed);
    this.asideTarget.hidden = collapsed;
    this.openerTarget.hidden = !collapsed;
    this.collapseButtonTarget.setAttribute("aria-expanded", (!collapsed).toString());
    this.openerTarget.setAttribute("aria-expanded", (!collapsed).toString());

    // Manage backdrop on tablet
    if (this.hasBackdropTarget) {
      this.backdropTarget.hidden = collapsed || !this.isTablet();
    }

    if (persist) this.writeCollapsedCookie(collapsed);
    if (focus) requestAnimationFrame(() => focus.focus());
  }

  // Pointer users do not need focus moved onto the replacement button: doing
  // so paints a keyboard focus ring after a click. Keyboard and assistive-tech
  // activation dispatch a click with detail 0, where moving focus is essential
  // because the activated button is hidden by the state change.
  focusTargetFor(event, target) {
    return event?.detail === 0 ? target : null;
  }

  syncActiveState() {
    const homeItem = this.itemTargets.find((item) => item.dataset.item === "Home");
    if (!homeItem) return;

    let homeActive = false;
    try {
      const homeUrl = new URL(homeItem.href, window.location.href);
      homeActive = this.normalizePath(homeUrl.pathname) === this.normalizePath(window.location.pathname);
    } catch (_error) {
      return;
    }

    homeItem.classList.toggle(this.activeClassValue, homeActive);
    if (homeActive) {
      homeItem.setAttribute("aria-current", "page");
    } else {
      homeItem.removeAttribute("aria-current");
    }
  }

  normalizePath(path) {
    return path === "/" ? path : path.replace(/\/+$/, "");
  }

  isTablet() {
    return this.tabletMediaQuery?.matches ?? false;
  }

  collapsedCookieValue() {
    try {
      const cookie = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${this.cookieName()}=`));

      return cookie?.split("=").slice(1).join("=") || null;
    } catch (_error) {
      return null;
    }
  }

  writeCollapsedCookie(collapsed) {
    try {
      document.cookie = `${this.cookieName()}=${collapsed ? "1" : "0"}; Path=/; SameSite=Lax`;
    } catch (_error) {
      // The current DOM state still works when cookies are unavailable.
    }
  }

  cookieName() {
    return "community_owner_navigation_collapsed";
  }

  desktopSurface() {
    return this.surfaceValue === "desktop";
  }
}
